import { Request } from '../request';
import {
    ITreatmentPlan,
    CaseListFilter,
    MyCaseFilter,
    TPrescriptionListFilter,
    TPracticeState,
    TextVisitSnoozeReq,
    TPracticeListFilter,
    TPracticeRemindProviderReq,
} from 'types/textVisit';

export const getAllCaseList = (params: CaseListFilter) =>
    Request.post({ url: '/schedule-server/text/visit/all/list', params });

// Mock API for ineligible cases
export const getAllCaseListForIneligible = () => Request.get({ url: '/schedule-server/text/visit/mock' });

export const getMyCaseList = (params: MyCaseFilter) =>
    Request.post({ url: '/schedule-server/text/visit/my/list', params });

export const getMyCaseCount = () => Request.post({ url: '/schedule-server/text/visit/my/list/counts' });

export const getRecommendCase = () => Request.get({ url: '/schedule-server/text/visit/recommend' });

export const dismissRecommendCase = (caseId: number) =>
    Request.post({ url: `/schedule-server/text/visit/recommend/dismiss/${caseId}` });

export const getCaseDetail = (caseUuid: string, overrideProviderId?: number | string) =>
    Request.get({ url: `/schedule-server/text/visit/detail/${caseUuid}`, overrideProviderId });

export const requestAccess = (caseId: number) =>
    Request.post({ url: `/schedule-server/text/visit/access/request/${caseId}` });

export const acceptCase = (caseId: number) => Request.post({ url: `/schedule-server/text/visit/accept/${caseId}` });

export const releaseCase = (caseId: number, overrideProviderId?: number) =>
    Request.post({ url: `/schedule-server/text/visit/release/${caseId}`, overrideProviderId });

export const saveOrSubmitCase = (params: ITreatmentPlan, overrideProviderId?: number) =>
    Request.post({ url: '/schedule-server/text/visit/treatment/saveOrSubmit', params, overrideProviderId });

export const sendFollowUp = (params: { textVisitId: number; message: string }, overrideProviderId?: number) =>
    Request.post({ url: '/schedule-server/text/visit/follow/up/send', params, overrideProviderId });

export const getTextVisitEligibleStatus = () => Request.get({ url: '/schedule-server/text/visit/eligible/status' });

export const uploadTextVisitAttachment = (data: FormData) =>
    Request.post({
        url: '/oms-server/uploadPrivateFileByTextVisit',
        params: data,
        config: {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        },
    });

export interface ITextVisitPatientListParams {
    type: 'InProgress' | 'Upcoming' | 'PastDue' | 'Past';
    clientId: number;
    size: number;
    current: number;
    assessmentName?: string;
    orderKey?: string;
    orderType?: 'ASC' | 'DESC';
    providerId?: number;
    serviceTypeFilterMode?: 'ALL' | 'PRESCRIPTION_REFILL_ONLY' | 'NON_PRESCRIPTION_REFILL_ONLY';
    status?: string;
}

export const getTextVisitPatientList = (params: ITextVisitPatientListParams) =>
    Request.post({ url: '/schedule-server/text/visit/patient/list', params });

export const getScriptSureInvitationLink = () => Request.get({ url: '/schedule-server/script/sure/invitation/link' });

export const getScriptSureSessiontoken = () => Request.post({ url: '/schedule-server/script/sure/login' });

export const getScriptSurePatientId = (prospectId: string | number) =>
    Request.get({ url: `/schedule-server/script/sure/ssClientId/get/${prospectId}` });

export const getLatestVisitAllProspects = () => Request.get({ url: '/schedule-server/prospect/all/latest/visit/list' });

export const getLatestVisitTopProspects = () => Request.get({ url: '/schedule-server/prospect/top/latest/visit/list' });

export const getProviderPrescriptionList = (params: TPrescriptionListFilter) =>
    Request.post({ url: '/schedule-server/script/sure/provider/prescription/list', params });

export const savePracticeState = (params: Partial<TPracticeState>[]) =>
    Request.post({ url: '/schedule-server/provider/prescription/practice/save', params });

export const getPracticeState = () => Request.get({ url: '/schedule-server/provider/prescription/practice/list' });

export const getTextVisitRefundedInfo = (billingId: number) =>
    Request.get({ url: `/schedule-server/provider/billing/klarity-user-billing/${billingId}/refund-request` });

export const getPrescriptionsByTextVisit = (textVisitId: number) =>
    Request.get({ url: `/schedule-server/text/visit/prescription/list/${textVisitId}` });

export const clearUnreadMessage = (textVisitId: number) =>
    Request.post({ url: `/schedule-server/text/visit/unread/follow/up/count/clear/${textVisitId}` });

export const updatePatientCondition = (prospectId: number, params: any) =>
    Request.post({ url: `/schedule-server/patient/profile/health/save/${prospectId}`, params });

export const updatePatientID = (prospectId: number, params: any) =>
    Request.post({ url: `/schedule-server/patient/profile/identity/save/${prospectId}`, params });

export const snoozeTextVisit = (params: TextVisitSnoozeReq) =>
    Request.post({ url: '/schedule-server/text/visit/snooze', params });

export const getMedicationsByCondition = (conditions: string[]) =>
    Request.post({
        url: '/schedule-server/medicationCondition/batchQuery',
        params: conditions,
    });

export const allergySearch = (name: string) =>
    Request.get({ url: `/schedule-server/public/allergy/search?name=${name}` });

export const markTextVisitNotificationAsRead = () =>
    Request.post({ url: '/schedule-server/provider/notification/state/read/NEW_TEXT_VISIT' });

export const declineTextVisit = (params: { textVisitId: number; declineReason?: string }) =>
    Request.post({ url: '/schedule-server/text/visit/decline', params });

export const cancelRecurrencePrescriptionRefill = (params: { textVisitId: number; cancelAll: boolean }) =>
    Request.post({ url: `/schedule-server/text/visit/recurrence/cancel/${params.textVisitId}/${params.cancelAll}` });

// Practice-view (Owner / PM / Assistant) — org-wide, scope-respecting

export const getPracticeTextVisitList = (params: TPracticeListFilter) =>
    Request.post({ url: '/schedule-server/text/visit/practice/list', params });

export const getPracticeTextVisitCounts = (params?: TPracticeListFilter) =>
    Request.post({ url: '/schedule-server/text/visit/practice/list/counts', params });

export const remindPracticeProvider = (providerId: number, params: TPracticeRemindProviderReq) =>
    Request.post({ url: `/schedule-server/text/visit/practice/remind/provider/${providerId}`, params });

export const updateTextVisitStartAt = (params: { textVisitId: number; startAt: string }) =>
    Request.post({ url: '/schedule-server/text/visit/start-at', params });
